/**
 * Calendar API client.
 *
 * Covers the per-organization calendar endpoints, the cross-org filterable
 * feed, and the saved ICS-feed CRUD endpoints under /me/calendarFeeds.
 *
 * The public ICS endpoint (/calendar/feed/{token}.ics) is reached directly
 * via the URL — there's no API client for it.
 */

import { api } from './client';

// ---------------------------------------------------------------------------
// Per-organization calendar
// ---------------------------------------------------------------------------

export interface CalendarDayInput {
  specialDayTypeDescriptorId: number;
  /** ISO date YYYY-MM-DD */
  calendarDate: string;
}

export interface CalendarDayResponse {
  educationOrganizationCalendarDayId: number;
  specialDayTypeDescriptorId: number;
  specialDayTypeCode: string | null;
  /** ISO date YYYY-MM-DD */
  calendarDate: string;
}

export interface CalendarUpsert {
  studentStartDate?: string | null;
  studentEndDate?: string | null;
  studentTotalDays?: number | null;
  teacherStartDate?: string | null;
  teacherEndDate?: string | null;
  teacherTotalDays?: number | null;
  days: CalendarDayInput[];
}

export interface CalendarResponse {
  educationOrganizationCalendarId: number;
  educationOrganizationId: number;
  academicYearDescriptorId: number;
  academicYearCode: string | null;
  studentStartDate: string | null;
  studentEndDate: string | null;
  studentTotalDays: number | null;
  teacherStartDate: string | null;
  teacherEndDate: string | null;
  teacherTotalDays: number | null;
  days: CalendarDayResponse[];
  createdAt: string | null;
  modifiedAt: string | null;
}

export interface CalendarFeedItem {
  educationOrganizationCalendarDayId: number;
  educationOrganizationId: number;
  organizationShortName: string | null;
  organizationName: string | null;
  academicYearCode: string | null;
  specialDayTypeDescriptorId: number;
  specialDayTypeCode: string | null;
  /** ISO date YYYY-MM-DD */
  calendarDate: string;
}

export interface CalendarFeedListParams {
  districts?: number[];
  types?: number[];
  /** ISO date YYYY-MM-DD (inclusive) */
  from?: string;
  /** ISO date YYYY-MM-DD (inclusive) */
  to?: string;
}

export const calendarApi = {
  /**
   * Fetch the calendar for one (organization, academic year). Returns 404
   * if no calendar has been saved yet for that pair, which the caller
   * should treat as "empty" rather than an error.
   */
  get: async (
    educationOrganizationId: number,
    academicYear: string
  ): Promise<CalendarResponse> => {
    const params = new URLSearchParams({ academicYear });
    return api.get<CalendarResponse>(
      `/educationOrganizations/${educationOrganizationId}/calendar?${params}`
    );
  },

  /**
   * Upsert the calendar for one (organization, academic year). The server
   * diffs the days list against the stored rows.
   */
  upsert: async (
    educationOrganizationId: number,
    academicYear: string,
    payload: CalendarUpsert
  ): Promise<CalendarResponse> => {
    const params = new URLSearchParams({ academicYear });
    return api.put<CalendarResponse>(
      `/educationOrganizations/${educationOrganizationId}/calendar?${params}`,
      payload
    );
  },

  /**
   * Filterable cross-org feed. All filters are optional; null/empty arrays
   * mean "no filter". Backs both the in-app Calendar page and the ICS feed.
   */
  list: async (params: CalendarFeedListParams = {}): Promise<CalendarFeedItem[]> => {
    const search = new URLSearchParams();
    (params.districts ?? []).forEach((d) => search.append('districts', String(d)));
    (params.types ?? []).forEach((t) => search.append('types', String(t)));
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    const qs = search.toString();
    return api.get<CalendarFeedItem[]>(`/calendars${qs ? `?${qs}` : ''}`);
  },

  /**
   * Fetch the Power BI-style XLSX matrix export as a Blob, with the
   * same filters as `list`. The export always includes weekend columns
   * (shaded gray) so day counts read correctly per month.
   * Auth is handled by the apiFetch wrapper (Bearer token).
   */
  exportXlsx: async (params: CalendarFeedListParams = {}): Promise<Blob> => {
    const search = new URLSearchParams();
    (params.districts ?? []).forEach((d) => search.append('districts', String(d)));
    (params.types ?? []).forEach((t) => search.append('types', String(t)));
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    const qs = search.toString();
    return api.getBlob(`/calendars/export.xlsx${qs ? `?${qs}` : ''}`);
  },
};

// ---------------------------------------------------------------------------
// User calendar feeds (saved ICS subscription configurations)
// ---------------------------------------------------------------------------

export interface UserCalendarFeed {
  userCalendarFeedId: number;
  ownerUserId: number;
  name: string;
  districtFilter: number[] | null;
  dayTypeFilter: number[] | null;
  token: string;
  createdAt: string | null;
  modifiedAt: string | null;
}

export interface UserCalendarFeedCreate {
  name: string;
  districtFilter?: number[] | null;
  dayTypeFilter?: number[] | null;
}

export interface UserCalendarFeedUpdate {
  name?: string;
  districtFilter?: number[] | null;
  dayTypeFilter?: number[] | null;
}

export const calendarFeedsApi = {
  list: async (): Promise<UserCalendarFeed[]> => {
    return api.get<UserCalendarFeed[]>('/me/calendarFeeds');
  },

  create: async (data: UserCalendarFeedCreate): Promise<UserCalendarFeed> => {
    return api.post<UserCalendarFeed>('/me/calendarFeeds', data);
  },

  update: async (
    feedId: number,
    data: UserCalendarFeedUpdate
  ): Promise<UserCalendarFeed> => {
    return api.put<UserCalendarFeed>(`/me/calendarFeeds/${feedId}`, data);
  },

  delete: async (feedId: number): Promise<void> => {
    await api.delete<void>(`/me/calendarFeeds/${feedId}`);
  },

  rotateToken: async (feedId: number): Promise<UserCalendarFeed> => {
    return api.post<UserCalendarFeed>(`/me/calendarFeeds/${feedId}/rotateToken`, {});
  },

  /**
   * Build the public ICS URL for a feed token. Useful for "copy link" and
   * webcal:// buttons. Caller decides whether to use https:// or webcal://.
   */
  buildSubscriptionUrl: (token: string, scheme: 'https' | 'webcal' = 'https'): string => {
    const apiBase = import.meta.env.VITE_API_URL ?? '';
    const fullPath = `${apiBase}/calendar/feed/${encodeURIComponent(token)}.ics`;
    if (scheme === 'webcal') {
      // Replace https:// with webcal:// for one-click subscribe in calendar apps.
      return fullPath.replace(/^https?:\/\//, 'webcal://');
    }
    return fullPath;
  },
};
